import posthog from "posthog-js";
import { isTestMode } from "@ahead-health/core/system-checks/testMode";
import { safeLocalStorage } from "@/utils/safeStorage";

declare global {
  interface Window {
    test_data_layer?: unknown[];
  }
}

type EventNames =
  | "step_view"
  | "product_select"
  | "clinic_select"
  | "clinic_change"
  | "time_slot_select"
  | "time_slot_remove"
  | "booking_complete"
  | "purchase"
  | "language_change"
  | "market_changed" // Active market (country) switched in the header
  | "email_verified"
  | "phone_verified"
  | "phone_input_start"
  | "details_input_start"
  | "date_select"
  | "booking_user_verified"
  | "user_authenticated"
  | "auth_completed_at_confirmation"
  | "booking_notification_sent"
  | "product_selection_step"
  | "date_selection_step"
  | "confirm_step"
  | "details_input_step"
  | "product_preselect_url"
  | "view_item" // GA4 ecommerce event for viewing item details
  | "add_to_cart" // GA4 ecommerce event for adding items to cart
  | "remove_from_cart" // GA4 ecommerce event for removing items from cart
  | "view_cart" // GA4 ecommerce event for viewing cart
  | "email_captured" // Email capture step - user provided email
  | "email_capture_skipped" // Email capture step - user skipped
  | "session_conflict_shown" // Session conflict dialog displayed
  | "session_conflict_resolved" // Session conflict - user made a choice
  | "package_dialog_view" // Package detail dialog opened
  | "package_dialog_select" // Package selected from detail dialog
  | "membership_redemption" // Member completes booking using their entitlement
  | "Booking_complete_membership_addon" // Member adds paid extras at redemption
  | "membership_cta_click" // Clicked a membership cross-sell CTA inside the booking flow
  | "mri_scan_section_view" // Membership-first: the MRI upsell section scrolled into view
  | "mri_scan_attach" // Membership-first: a scan was added to the membership cart
  | "mri_scan_detach" // Membership-first: that scan was taken back out
  | "mri_comparison_open" // Membership-first: the package comparison table was opened
  | "membership_cadence_change" // Cadence changed after the plan was picked (cart or payment step)
  | "payment_options_view" // Details-page payment block (pay-later vs become-a-member upsell) shown to an eligible visitor (AHD-7022)
  | "payment_mode_select" // Details-page payment option picked: pay-later ("once") or become-a-member ("yearly")
  | "membership_checkout_initiated" // Membership Stripe checkout opened in the booking flow (cadence flow or details upsell) — same event name the membership app fires at its checkout, so cross-app funnels aggregate
  | "persisted_selection_invalidated"; // Persisted date/time selection invalidated on session rehydrate / clinic or product change (AHD-6457)

interface AnalyticsEvent {
  event: EventNames;
  props?: Record<string, unknown>;
}

// GA4 Ecommerce item interface
interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  currency?: string;
  quantity?: number;
}

// Suppress tracking on localhost, on analytics opt-out, and for synthetic
// test sessions. Every analytics capture must honor this — including captures
// that bypass trackEvent, like booking_step in bookingFlowStore — otherwise
// test traffic pollutes PostHog funnels while completions stay guarded.
// Test sessions are marked two ways: testUserId= in the URL (legacy), and
// the ahead-core runner's testId= which isTestMode() also reads back from
// localStorage so it survives navigations that drop the query string.
export const isAnalyticsSuppressed = () => {
  if (typeof window === "undefined") {
    return false;
  }
  const hostname = window.location.hostname;
  const isLocalhostDomain =
    hostname === "localhost" || hostname === "127.0.0.1";

  // Check localStorage for analytics opt-out
  const analyticsStorageValue = safeLocalStorage.getItem("analytics");
  const isAnalyticsDisabled = analyticsStorageValue === "no";

  // Check for testUserId in URL params (test mode should not track analytics)
  const hasTestUserId = window.location.search.includes("testUserId=");

  return (
    isLocalhostDomain || isAnalyticsDisabled || hasTestUserId || isTestMode()
  );
};

const isE2EMode = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.search.includes("e2e=true");
};

/**
 * Identity fields that must never reach PostHog.
 *
 * These are the Meta advanced-matching inputs produced by
 * `buildFacebookUserData`, plus the bare `email` and the booking-flow
 * `formData` snapshot. They belong in the GTM dataLayer — Meta hashes them
 * there — but the same object was handed straight to `posthog.capture`, which
 * stored them raw. In the 30 days before this change PostHog held 274 raw
 * phone numbers and dates of birth on `booking_complete`, and 559 full
 * `formData` objects (name, email, phone, birthday, address, free-text notes)
 * on `booking_step`. AHD-9080.
 *
 * Typed as an exhaustive record over `keyof FacebookUserData`, so adding a
 * field to that interface without making a decision about it here fails the
 * build — a denylist that cannot silently fall behind the type it guards.
 */
const POSTHOG_PII_KEYS: Record<
  keyof FacebookUserData | "email" | "formData" | "entryEmail",
  true
> = {
  user_email: true,
  user_phone: true,
  user_firstName: true,
  user_lastName: true,
  user_gender: true,
  user_dateOfBirth: true,
  user_city: true,
  user_state: true,
  user_zip: true,
  user_country: true,
  user_external_id: true,
  email: true,
  formData: true,
  entryEmail: true,
};

/**
 * Internal/test booking detection, from the email BEFORE it is stripped.
 *
 * AHD-9080 removed raw emails from PostHog payloads — which also removed the
 * only signal analytics had for excluding internal test bookings (the
 * dashboard queries filtered on `properties.email`). `is_internal` is the
 * non-PII replacement: the domain list mirrors the exclusion regex the
 * PostHog CAC insights already use, and the local-part check matches the
 * team's test-address conventions (test@…, test+tag@…).
 */
const INTERNAL_EMAIL_DOMAIN =
  /@([a-z0-9-]+\.)*(aheadhealth\.com|ahead\.clinic|example\.com|testmail\.app)$/i;

// "test" as a whole token in the local part (test@, test1@, john.test@,
// test+tag@) — NOT as a substring, which would permanently mislabel real
// customers like attest@… / protest.…@…. Queries can be loosened later; a
// stored flag can't.
const TEST_LOCAL_PART = /(^|[._+-])test($|[._+\d-])/;

export const isInternalEmail = (email: string): boolean => {
  const normalized = email.trim().toLowerCase();
  if (INTERNAL_EMAIL_DOMAIN.test(normalized)) return true;
  const at = normalized.indexOf("@");
  return at > 0 && TEST_LOCAL_PART.test(normalized.slice(0, at));
};

/** The email as it appears in any of the payload shapes handed to
 * `stripPiiForPostHog`: top-level `email`/`user_email` (booking_complete,
 * ecommerce events) or nested `formData.email` (booking_step snapshots). */
const extractEmail = (props: Record<string, unknown>): string | null => {
  const formData = props.formData;
  const candidates = [
    props.email,
    props.user_email,
    props.entryEmail,
    formData && typeof formData === "object" && "email" in formData
      ? (formData as { email?: unknown }).email
      : undefined,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.includes("@")) {
      return candidate;
    }
  }
  return null;
};

/**
 * Drop identity fields from an analytics payload before it goes to PostHog.
 *
 * Deliberately applied ONLY to the PostHog call — the dataLayer push keeps the
 * full object, because Meta advanced matching depends on those fields being
 * present there. Country and gender go too: PostHog already derives country
 * from `$geoip_country_code`, and neither is worth re-introducing a
 * per-field judgement call into a list whose whole value is being exhaustive.
 */
export const stripPiiForPostHog = (props: object): Record<string, unknown> => {
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (key in POSTHOG_PII_KEYS) continue;
    clean[key] = value;
  }
  // Derived from the email before the strip: absence means "unknown" (e.g. a
  // booking_step before the form is filled), so the flag is only set when an
  // email was actually present. Dashboard filters treat a missing flag as
  // not internal.
  const email = extractEmail(props as Record<string, unknown>);
  if (email !== null) {
    clean.is_internal = isInternalEmail(email);
  }
  return clean;
};

export const trackEvent = ({ event, props = {} }: AnalyticsEvent) => {
  // Ensure we're in a browser environment
  if (typeof window === "undefined") {
    return;
  }

  const inE2EMode = isE2EMode();
  const suppressed = isAnalyticsSuppressed();

  // On production, push to dataLayer
  if (!suppressed) {
    try {
      // Initialize dataLayer if it doesn't exist
      window.dataLayer = window.dataLayer || [];

      // Create the event object
      const eventObject = {
        event,
        event_time: new Date().toISOString(),
        ...props,
      };

      // Push to dataLayer
      window.dataLayer.push(eventObject);

      // Send to PostHog — identity fields stripped; the dataLayer copy above
      // keeps them for Meta advanced matching.
      posthog.capture(event, stripPiiForPostHog(props));

      // Log for debugging
      console.log("📊 Analytics event:", eventObject);
    } catch (error) {
      // Log any errors but don't throw them to prevent app crashes
      console.error("Error tracking event:", error);
    }
    return;
  }

  // On localhost in E2E mode, push to test_data_layer for testing
  if (suppressed && inE2EMode) {
    try {
      window.test_data_layer = window.test_data_layer || [];

      const eventObject = {
        event,
        event_time: new Date().toISOString(),
        ...props,
      };

      window.test_data_layer.push(eventObject);
      console.log("📊 [E2E] Analytics event:", eventObject);
    } catch (error) {
      console.error("Error tracking event in E2E mode:", error);
    }
    return;
  }

  // On localhost without E2E mode, just log
  console.log(
    "📊 [DEV] Analytics tracking is disabled on localhost (not in E2E mode).",
  );
};

// Helper function to deeply uppercase all `currency` fields
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeCurrency = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map(normalizeCurrency);
  } else if (data && typeof data === "object") {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        if (key.toLowerCase() === "currency" && typeof value === "string") {
          return [key, value.toUpperCase()];
        }
        return [key, normalizeCurrency(value)];
      }),
    );
  }
  return data;
};

const trackEcommerceEvent = (
  event: EventNames,
  ecommerceData: Record<string, unknown>,
  additionalProps?: Record<string, unknown>,
) => {
  // Ensure we're in a browser environment
  if (typeof window === "undefined") {
    return;
  }

  const inE2EMode = isE2EMode();
  const suppressed = isAnalyticsSuppressed();

  try {
    // Normalize all currency fields (deep)
    const normalizedEcommerceData = normalizeCurrency(ecommerceData);

    const eventObject = {
      event,
      event_time: new Date().toISOString(),
      ecommerce: normalizedEcommerceData,
      ...additionalProps,
    };

    // On production, push to dataLayer
    if (!suppressed) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(eventObject);

      // Send to PostHog — identity fields stripped; the dataLayer copy above
      // keeps them for Meta advanced matching.
      posthog.capture(
        event,
        stripPiiForPostHog({
          ecommerce: normalizedEcommerceData,
          ...additionalProps,
        }),
      );

      console.log("📊 Ecommerce event:", eventObject);
      return;
    }

    // On localhost in E2E mode, push to test_data_layer for testing
    if (suppressed && inE2EMode) {
      window.test_data_layer = window.test_data_layer || [];
      window.test_data_layer.push(eventObject);
      console.log("📊 [E2E] Ecommerce event:", eventObject);
      return;
    }

    // On localhost without E2E mode, just log
    console.log(
      "📊 [DEV] Analytics tracking is disabled on localhost (not in E2E mode).",
    );
  } catch (error) {
    console.error("Error tracking ecommerce event:", error);
  }
};

// GA4 Ecommerce tracking functions
export const trackViewItem = (item: GA4Item) => {
  trackEcommerceEvent("view_item", {
    currency: item.currency || "CHF",
    value: item.price || 0,
    items: [item],
  });
};

export const trackAddToCart = (
  items: GA4Item[],
  totalValue: number,
  currency: string = "CHF",
) => {
  trackEcommerceEvent("add_to_cart", {
    currency,
    value: totalValue,
    items,
  });
};

export const trackRemoveFromCart = (
  items: GA4Item[],
  totalValue: number,
  currency: string = "CHF",
) => {
  trackEcommerceEvent("remove_from_cart", {
    currency,
    value: totalValue,
    items,
  });
};

export const trackViewCart = (
  items: GA4Item[],
  totalValue: number,
  currency: string = "CHF",
) => {
  trackEcommerceEvent("view_cart", {
    currency,
    value: totalValue,
    items,
  });
};

export const trackProductSelect = (
  items: GA4Item[],
  totalValue: number,
  currency: string = "CHF",
) => {
  trackEcommerceEvent("product_select", {
    currency,
    value: totalValue,
    items,
  });
};

export const trackBookingComplete = (
  items: GA4Item[],
  totalValue: number,
  transactionId: string,
  currency: string = "CHF",
  additionalProps?: Record<string, unknown>,
) => {
  trackEcommerceEvent(
    "booking_complete",
    {
      currency,
      value: totalValue,
      transaction_id: transactionId,
      items,
    },
    additionalProps,
  );
};

// Helper function to extract and format user data for Facebook Conversion API
export interface FacebookUserData {
  user_email?: string;
  user_phone?: string;
  user_firstName?: string;
  user_lastName?: string;
  user_gender?: string;
  user_dateOfBirth?: string;
  user_city?: string;
  user_state?: string;
  user_zip?: string;
  user_country?: string;
  user_external_id?: string;
}

export const buildFacebookUserData = (
  formData: Record<string, unknown>,
): FacebookUserData => {
  return {
    user_email: formData.email ? String(formData.email) : undefined,
    user_phone: formData.phone ? String(formData.phone) : undefined,
    user_firstName: formData.name
      ? String(formData.name).split(" ")[0]
      : undefined,
    user_lastName: formData.name
      ? String(formData.name).split(" ").slice(1).join(" ")
      : undefined,
    user_gender: formData.genderAtBirth
      ? String(formData.genderAtBirth)
      : undefined,
    user_dateOfBirth: formData.birthday ? String(formData.birthday) : undefined,
    user_city: formData.city ? String(formData.city) : undefined,
    user_state: formData.street ? String(formData.street) : undefined,
    user_zip: formData.zipCode ? String(formData.zipCode) : undefined,
    user_country: formData.country ? String(formData.country) : undefined,
  };
};

// ── Membership redemption helpers ─────────────────────────────────────────────

export const trackMembershipRedemption = (
  items: GA4Item[],
  transactionId: string,
  additionalProps?: Record<string, unknown>,
) => {
  // Redemption is always CHF 0 for the entitled product
  trackEcommerceEvent(
    "membership_redemption",
    {
      currency: "CHF",
      value: 0,
      transaction_id: transactionId,
      items,
    },
    additionalProps,
  );
};

export const trackMembershipAddon = (
  items: GA4Item[],
  totalValue: number,
  transactionId: string,
  currency: string = "CHF",
  additionalProps?: Record<string, unknown>,
) => {
  trackEcommerceEvent(
    "Booking_complete_membership_addon",
    {
      currency,
      value: totalValue,
      transaction_id: transactionId,
      items,
    },
    additionalProps,
  );
};

/**
 * Membership cross-sell CTAs live inside the booking flow but link out to
 * membership.aheadhealth.com / www in a new tab. Those clicks were previously
 * untracked, so the only way to see booking→membership handoff was a
 * person-level cross-domain funnel. This fires the click directly.
 */
export const trackMembershipCtaClick = (
  cta: "become_a_member" | "learn_more" | "find_my_plan",
  destination: "membership_signup" | "marketing_membership" | "membership_quiz",
  href: string,
  language: string,
) => {
  trackEvent({
    event: "membership_cta_click",
    props: { cta, destination, href, location: "product_step", language },
  });
};

/**
 * The membership-first MRI upsell (MriScanSection). It replaced the dedicated
 * `mri-crosssell` step, and a step is the one thing that reports itself: that
 * step's `step_view` was how we knew how many members even saw the offer. A
 * section on an existing page fires nothing, so without these the middle of
 * the funnel goes dark — and at a ~4% attach rate the intermediate numbers are
 * the only ones that move fast enough to read.
 *
 * View is fired on intersection, not on mount: the section sits below the
 * add-ons, so mounting proves nothing about whether it was seen.
 */
export const trackMriScanSectionView = (tiers: string[]) => {
  trackEvent({
    event: "mri_scan_section_view",
    props: { tiers, tier_count: tiers.length },
  });
};

/**
 * `source` separates the ways in and out: the section cards, the comparison
 * table, and the bin in the order summary.
 *
 * `cart` is not a nicety. It is how a scan is actually taken back out — the
 * section card only un-attaches if the user scrolls back up to it — so without
 * it the funnel records attaches with almost no detaches and reads the attach
 * rate as permanent.
 */
export const trackMriScanToggle = (
  attached: boolean,
  tier: string,
  priceCents: number | null,
  source: "section_card" | "comparison_modal" | "cart",
) => {
  trackEvent({
    event: attached ? "mri_scan_attach" : "mri_scan_detach",
    props: {
      tier,
      price_chf: priceCents === null ? null : priceCents / 100,
      source,
    },
  });
};

export const trackMriComparisonOpen = (attachedTier: string | null) => {
  trackEvent({ event: "mri_comparison_open", props: { attachedTier } });
};

/**
 * Cadence changed after the plan was already chosen — from the payment step or
 * from the membership line in the cart.
 *
 * The cadence IS the subscription price (299 / 499 / 899), so this is the one
 * in-flow action that changes recurring revenue, and `membership_plan_select`
 * only covers the first pick on the cadence step. Upgrades and downgrades made
 * later were indistinguishable from never having changed at all.
 */
export const trackMembershipCadenceChange = (
  from: string | null,
  to: string,
  source: "cart" | "payment_step",
) => {
  trackEvent({
    event: "membership_cadence_change",
    props: { from, to, source, changed: from !== to },
  });
};

export const trackCountrySwitched = (from: string, to: string) => {
  trackEvent({ event: "market_changed", props: { from, to } });
};

/** Prices the payment-option cards display (voucher-applied, in cents) —
 *  attached to the upsell events so acceptance can be segmented by the offer
 *  the visitor actually saw. */
export interface PaymentOptionsPricing {
  full_total_cents: number;
  member_total_cents: number;
  savings_cents: number;
}

/**
 * Details-page membership upsell (AHD-7022). Before these trackers, accepting
 * the upsell only set `selectedPaymentMode` — a field neither captured in the
 * booking_step snapshot nor tracked by any event — so upsell-driven membership
 * purchases were invisible in PostHog (indistinguishable from plain blood
 * bookings). The funnel is now: `payment_options_view` (offer shown) →
 * `payment_mode_select` (option picked) → `membership_checkout_initiated`
 * (Stripe modal opened) → `booking_step[confirmation,
 * membershipCheckoutInitiated=upsell]` (paid — see the snapshot field in
 * `sessionState.ts`).
 */
export const trackPaymentOptionsView = (
  plan: string,
  pricing: PaymentOptionsPricing,
  language: string,
) => {
  trackEvent({
    event: "payment_options_view",
    props: { location: "details_step", plan, ...pricing, language },
  });
};

export const trackPaymentModeSelect = (
  mode: "once" | "yearly",
  trigger: "option_card" | "cadence_modal",
  plan: string,
  pricing: PaymentOptionsPricing,
  language: string,
) => {
  trackEvent({
    event: "payment_mode_select",
    props: { mode, trigger, plan, ...pricing, language },
  });
};

/**
 * Fires when the membership Stripe checkout (embedded Payment Element modal)
 * opens — for BOTH membership paths: the cadence flow (`source: "cadence"`)
 * and the details-page upsell (`source: "upsell"`). `session_id` joins it to
 * the same session's `booking_step` events.
 */
export const trackMembershipCheckoutInitiated = (
  source: "cadence" | "upsell",
  plan: string,
  memberTotalCents: number,
  language: string,
  sessionId: string | null,
) => {
  trackEvent({
    event: "membership_checkout_initiated",
    props: {
      source,
      plan,
      member_total_cents: memberTotalCents,
      language,
      session_id: sessionId,
    },
  });
};

export function getBookingItemCategory(
  selectedProductIds: string[],
  allProducts: Array<{
    id: string;
    product_type?: string | null;
    package_types?: string[] | null;
  }>,
  isRedemption: boolean,
): string {
  if (isRedemption) return "Redemption";
  const isBloodOnly = selectedProductIds.some((id) => {
    const p = allProducts.find((prod) => prod.id === id);
    return (
      p?.product_type === "package" && p?.package_types?.includes("blood-only")
    );
  });
  return isBloodOnly ? "Blood test" : "MRI Package";
}
